import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Button, SectionCard, Select, DataTable, LinkCell, formatDate, type Column } from '@datahub/design-system'
import { get, downloadCsv } from '../../lib/api'
import { entityPath } from '../../lib/paths'
import { ListPage } from '../AdminShell'
import { StatusTag } from '../../components/StatusTag'

interface Summary {
  companiesInvited: number
  responseRate: number
  onTimeRate: number
  medianDaysToSubmit: number | null
  medianCompletionPct: number | null
  rowsReported: Record<string, number>
  confidentialRows: number
  tabsSkippedRate: number
  nothingToReportRate: number
  neverResponded: number
  reminderLift: Record<string, number>
  copyToCoreBacklog: { rows: number; oldestDays: number | null }
  emailHealth: { delivered: number; opened: number; bounced: number; unsubscribed: number }
}

interface MatrixCell { cycleId: string | null; status: string; submittedAt: string | null; completionPct: number; versions: number }
interface MatrixRow { companyId: string; companyName: string; entityType: string; cadence: string | null; cells: Record<string, MatrixCell> }

/** RPT-05: summary metrics plus a company-by-period matrix. */
export function Reports() {
  const navigate = useNavigate()
  const [cadence, setCadence] = useState('')
  const query = new URLSearchParams(cadence ? { cadence } : {}).toString()

  const summary = useQuery({ queryKey: ['report-summary', cadence], queryFn: () => get<Summary>(`/admin/reports/summary?${query}`) })
  const matrix = useQuery({ queryKey: ['report-matrix', cadence], queryFn: () => get<{ periods: string[]; rows: MatrixRow[] }>(`/admin/reports/matrix?${query}`) })

  const metrics: [string, string][] = summary.data
    ? [
        ['Companies invited', String(summary.data.companiesInvited)],
        ['Response rate', `${summary.data.responseRate}%`],
        ['On-time rate', `${summary.data.onTimeRate}%`],
        ['Median days to submit', summary.data.medianDaysToSubmit === null ? '-' : String(summary.data.medianDaysToSubmit)],
        ['Median completion at submission', summary.data.medianCompletionPct === null ? '-' : `${summary.data.medianCompletionPct}%`],
        ['Rows reported', Object.entries(summary.data.rowsReported).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(', ') || '-'],
        ['Confidential rows', String(summary.data.confidentialRows)],
        ['Submissions using a tab toggle', `${summary.data.tabsSkippedRate}%`],
        ['Nothing to report', `${summary.data.nothingToReportRate}%`],
        ['Never responded', String(summary.data.neverResponded)],
        ['Reminder lift', Object.entries(summary.data.reminderLift).map(([k, v]) => `${k}: ${v}`).join(', ') || '-'],
        ['Rows awaiting copy to Core', `${summary.data.copyToCoreBacklog.rows}${summary.data.copyToCoreBacklog.oldestDays !== null ? ` (oldest ${summary.data.copyToCoreBacklog.oldestDays} days)` : ''}`],
        ['Email delivered / opened / bounced', `${summary.data.emailHealth.delivered} / ${summary.data.emailHealth.opened} / ${summary.data.emailHealth.bounced}`],
        ['Unsubscribed contacts', String(summary.data.emailHealth.unsubscribed)],
      ]
    : []

  const periods = matrix.data?.periods ?? []
  const columns: Column<MatrixRow>[] = [
    { key: 'company', header: 'Company', width: 240, sticky: true,
      render: (r) => <LinkCell onClick={() => navigate(entityPath(r.entityType, r.companyId))}>{r.companyName}</LinkCell> },
    { key: 'cadence', header: 'Cadence', width: 120, render: (r) => r.cadence ?? '-' },
    ...periods.map((period) => ({
      key: period,
      header: period,
      width: 170,
      render: (r: MatrixRow) => {
        const cell = r.cells[period]
        if (!cell) return '-'
        return (
          <span title={`${cell.completionPct}% complete${cell.submittedAt ? `, submitted ${formatDate(cell.submittedAt)}` : ''}${cell.versions > 1 ? `, ${cell.versions} versions` : ''}`}>
            <StatusTag status={cell.status} />
          </span>
        )
      },
    })),
  ]

  return (
    <ListPage
      title="Reports"
      actions={<Button variant="outline" size="medium" onClick={() => void downloadCsv(`/admin/exports/tracker?${query}`, 'datahub-report.csv')}>Export</Button>}
    >
      <div className="flex items-center flex-shrink-0 bg-background-main" style={{ height: 54, padding: '0 20px', gap: 12, borderBottom: '0.5px solid #4B5563' }}>
        <div style={{ width: 200 }}>
          <Select
            size="small" ariaLabel="Filter by cadence" placeholder="All cadences" value={cadence} onChange={setCadence}
            options={[{ value: '', label: 'All cadences' }, ...['monthly', 'quarterly', 'semi_annual', 'annual'].map((s) => ({ value: s, label: s }))]}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-background-main" style={{ padding: 20 }}>
        <div className="flex flex-col" style={{ gap: 20 }}>
          <SectionCard title="Summary">
            <div className="flex flex-col">
              {summary.isLoading ? (
                <p className="font-mulish text-text-support" style={{ fontSize: 12, padding: '12px 20px' }}>Loading…</p>
              ) : (
                metrics.map(([label, value]) => (
                  <div key={label} className="flex items-start" style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
                    <span className="font-mulish text-white flex-shrink-0" style={{ fontSize: 12, width: '42%' }}>{label}</span>
                    <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1 }}>{value}</span>
                  </div>
                ))
              )}
            </div>
          </SectionCard>

          <SectionCard title="Companies by period">
            <DataTable
              columns={columns}
              rows={matrix.data?.rows ?? []}
              rowKey={(r) => r.companyId}
              caption="Companies by period"
              emptyMessage="No cycles in this range"
            />
          </SectionCard>
        </div>
      </div>
    </ListPage>
  )
}
